import { IconMessageCircle } from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ROUTES } from 'constants/routePaths';
import { useAuth } from 'hooks/useAuth';
import { centerService } from 'services/centerService';
import { chatService } from 'services/chatService';
import { getValidCenterId } from 'services/rolesEndpoint';
import socket from 'services/socket';
import { ensureNotifyPermission, notifyDesktop } from 'util/browserNotify';

/**
 * Header icon linking to the Messages page, with a total-unread badge.
 *
 * Kept live over the WebSocket: any `chat.message` re-reads the conversation
 * list (cheap) so the badge stays current, AND pops an in-app toast for a new
 * message from someone else — unless the chat is muted or you're already
 * looking at that conversation. Clicking the toast opens the conversation.
 *
 * `chat.message` is the raw real-time feed the chat UI itself depends on
 * (badge, message list) — it fires regardless of notification preferences,
 * same as it always has. Only the desktop-popup SIDE EFFECT is gated on the
 * center's "Chat Messages" in-app preference: unlike the bell (which listens
 * for `notification.new`, already suppressed server-side when disabled),
 * this event bypasses that pipeline entirely, so nothing upstream stops it.
 */
export function MessagesButton() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  // Refs so the socket handler (subscribed once) always reads current values.
  const mutedRef = useRef(new Set());
  const locRef = useRef(location);
  locRef.current = location;
  const meRef = useRef(user);
  meRef.current = user;
  // Defaults to allowed — matches the backend's own fail-open behavior (no
  // center, or the preference hasn't loaded yet, means "don't suppress").
  const chatDesktopAllowedRef = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const res = await chatService.listConversations();
      const convs = Array.isArray(res?.data) ? res.data : [];
      mutedRef.current = new Set(convs.filter((c) => c.is_muted).map((c) => String(c.id)));
      // The conversation you're currently viewing counts as read, even if the
      // server's unread_count hasn't caught up with the just-marked-read call yet.
      const loc = locRef.current;
      const activeConvId = loc.pathname === ROUTES.MESSAGES
        ? new URLSearchParams(loc.search).get('c') : null;
      // Muted (and the actively-viewed) conversations don't contribute to the badge.
      setUnread(convs.reduce((sum, c) => {
        if (c.is_muted) return sum;
        if (activeConvId && String(c.id) === String(activeConvId)) return sum;
        return sum + (c.unread_count || 0);
      }, 0));
    } catch {
      /* best-effort — keep the previous badge on error */
    }
  }, []);

  useEffect(() => {
    const centerId = getValidCenterId();
    if (!centerId) return;
    centerService
      .getNotificationPreferences(centerId)
      .then((r) => {
        const groups = Array.isArray(r?.data) ? r.data : [];
        const chatItem = groups
          .find((g) => g.key === 'chat')
          ?.items?.find((i) => i.item === 'chat_message');
        // Absent (group/item not found, e.g. an older cached response) also
        // defaults to allowed, same as everywhere else in this system.
        if (chatItem) chatDesktopAllowedRef.current = !!chatItem.in_app;
      })
      .catch(() => {
        /* best-effort — a failed fetch must not block desktop notifications */
      });
  }, []);

  useEffect(() => {
    refresh();
    // Ask for desktop-notification permission up front so live chat messages
    // can pop a real Chrome notification instead of an in-app-only toast.
    ensureNotifyPermission();
    const onMessage = (data) => {
      refresh();
      if (!data) return;
      const myId = String(meRef.current?.id || '');
      if (myId && String(data.sender_id) === myId) return;        // my own message
      if (mutedRef.current.has(String(data.conversation_id))) return; // muted chat
      // Stay quiet only when I'm actively reading this exact conversation with
      // the tab focused — anything else (other conversation, or tab in the
      // background, even on the Messages route) still fires a notification.
      const loc = locRef.current;
      const viewingThis = loc.pathname === ROUTES.MESSAGES
        && new URLSearchParams(loc.search).get('c') === String(data.conversation_id);
      if (viewingThis && document.visibilityState === 'visible') return;
      if (!chatDesktopAllowedRef.current) return; // center disabled Chat Messages in-app

      const body = (data.body || '').replace(/\s+/g, ' ').trim();
      const preview = body.slice(0, 120) || (data.attachments?.length ? '📎 Attachment' : '');
      notifyDesktop(`💬 ${data.sender_name || 'New message'}`, {
        body: preview,
        // Same tag the backend puts on the chat push, so the two copies collapse.
        tag: `chat-${data.conversation_id}`,
        linkUrl: `${ROUTES.MESSAGES}?c=${data.conversation_id}`,
        onClick: () => navigate(`${ROUTES.MESSAGES}?c=${data.conversation_id}`),
      });
    };
    const off = socket.on('chat.message', onMessage);
    return off;
  }, [refresh, navigate]);

  return (
    <button
      type="button"
      onClick={() => navigate(ROUTES.MESSAGES)}
      title="Messages"
      aria-label={unread > 0 ? `Messages, ${unread} unread` : 'Messages'}
      className="relative flex h-[38px] w-[38px] items-center justify-center rounded-full text-silver transition-colors hover:bg-black/[0.06] hover:text-ink"
    >
      <IconMessageCircle className="h-5 w-5" />
      {unread > 0 && (
        <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-brand px-1 text-[10px] font-semibold text-paper">
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </button>
  );
}

export default MessagesButton;
